import RecordedSportStats from "../components/RecordedSportStats";
// src/pages/NflGameDetail.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { API_BASE, api, fetchFixtureEdges } from "../api";
import { isAllowedPublicEdge } from "../utils/publicEdge";
import MatchupModelSection from "../components/MatchupModelSection";
import FixtureAccessPill from "../components/FixtureAccessPill";

const C = {
  pageBg: "#f0fdf4", cardBg: "#ffffff", fieldLine: "#16a34a", yardLine: "#22c55e",
  endZone: "#15803d", accentDark: "#166534", accentLight: "#bbf7d0",
  textPri: "#1e293b", textSub: "#475569", textMut: "#94a3b8",
  green: "#16a34a", greenLt: "rgba(22,163,74,0.10)", red: "#dc2626", redLt: "rgba(220,38,38,0.10)",
};

function FieldBg() {
  return (
    <svg viewBox="0 0 900 400" preserveAspectRatio="xMidYMid slice"
      style={{ position: "fixed", inset: 0, width: "100%", height: "100%", opacity: 0.10, pointerEvents: "none", zIndex: 0 }}>
      <rect x="40" y="60" width="820" height="280" fill="none" stroke={C.fieldLine} strokeWidth="3" />
      <rect x="40" y="60" width="80" height="280" fill="none" stroke={C.endZone} strokeWidth="2" opacity="0.3" />
      <rect x="780" y="60" width="80" height="280" fill="none" stroke={C.endZone} strokeWidth="2" opacity="0.3" />
      <line x1="120" y1="60" x2="120" y2="340" stroke={C.endZone} strokeWidth="2.5" />
      <line x1="780" y1="60" x2="780" y2="340" stroke={C.endZone} strokeWidth="2.5" />
      {[0,1,2,3,4,5,6,7,8,9,10].map(i => <line key={i} x1={120+i*66} y1="60" x2={120+i*66} y2="340" stroke={C.yardLine} strokeWidth={i===5?3:1.5} />)}
    </svg>
  );
}

function Football({ size = 24 }) {
  return (
    <svg width={size} height={size * 0.6} viewBox="0 0 24 15" style={{ flexShrink: 0 }}>
      <ellipse cx="12" cy="7.5" rx="10" ry="6" fill="#8b4513" stroke="#654321" strokeWidth="1.2" />
      <line x1="12" y1="2" x2="12" y2="13" stroke="#fff" strokeWidth="0.8" />
      <line x1="9" y1="7.5" x2="15" y2="7.5" stroke="#fff" strokeWidth="0.6" />
    </svg>
  );
}

const playerStatLines = (player) => {
  const pos = String(player.position || "").toUpperCase();
  if (pos === "QB") return [["Pass yards", player.passing_yards], ["Pass TD", player.passing_tds], ["INT", player.passing_interceptions], ["Comp/Att", `${player.completions}/${player.attempts}`]];
  if (["RB", "FB"].includes(pos)) return [["Rush yards", player.rushing_yards], ["Carries", player.carries], ["Rush TD", player.rushing_tds], ["Rec yards", player.receiving_yards]];
  if (["WR", "TE"].includes(pos)) return [["Rec yards", player.receiving_yards], ["Receptions", player.receptions], ["Targets", player.targets], ["Rec TD", player.receiving_tds]];
  if (["K", "P"].includes(pos)) return [["FG", `${player.fg_made}/${player.fg_att}`], ["Long", player.fg_long], ["PAT", `${player.pat_made}/${player.pat_att}`], ["Games", player.games]];
  return [["Solo tackles", player.def_tackles_solo], ["Assists", player.def_tackle_assists], ["Sacks", player.def_sacks], ["INT", player.def_interceptions]];
};

const playerPerGame = (player) => {
  const games = Math.max(1, Number(player.games) || 1);
  const pos = String(player.position || "").toUpperCase();
  if (pos === "QB") return `${(Number(player.passing_yards || 0) / games).toFixed(1)} pass YPG`;
  if (["RB", "FB"].includes(pos)) return `${(Number(player.rushing_yards || 0) / games).toFixed(1)} rush YPG`;
  if (["WR", "TE"].includes(pos)) return `${(Number(player.receiving_yards || 0) / games).toFixed(1)} rec YPG`;
  if (["K", "P"].includes(pos)) return `${(Number(player.fg_made || 0) / games).toFixed(1)} FG/game`;
  return `${((Number(player.def_tackles_solo || 0) + Number(player.def_tackle_assists || 0)) / games).toFixed(1)} tackles/game`;
};

function PlayerStatsPanel({ fixture, season, fixtureId }) {
  const [payloads, setPayloads] = useState(null);
  const [error, setError] = useState("");
  const [teamFilter, setTeamFilter] = useState("ALL");
  const [positionFilter, setPositionFilter] = useState("ALL");
  useEffect(() => {
    let cancelled = false;
    if (!fixture?.home_team_code || !fixture?.away_team_code || !season) return undefined;
    setPayloads(null); setError("");
    Promise.all([
      api.get(`/api/fixtures/nfl/team/${fixture.away_team_code}/players`, { params: { season } }),
      api.get(`/api/fixtures/nfl/team/${fixture.home_team_code}/players`, { params: { season } }),
    ]).then(([away, home]) => !cancelled && setPayloads([away.data, home.data])).catch((e) => !cancelled && setError(e?.message || "Could not load players"));
    return () => { cancelled = true; };
  }, [fixture, season]);
  if (error) return <div style={emptyPanelStyle}>{error}</div>;
  if (!payloads) return <div style={emptyPanelStyle}>Loading player stats…</div>;
  const positions = [...new Set(payloads.flatMap((team) => team.players.map((player) => player.position_group || player.position)).filter(Boolean))];
  const visibleTeams = payloads.filter((team) => teamFilter === "ALL" || team.team === teamFilter);
  return <div style={{ display: "grid", gap: 14 }}>
    <div style={{ position: "sticky", top: 138, zIndex: 4, display: "flex", gap: 10, flexWrap: "wrap", padding: 12, background: "rgba(255,255,255,.96)", border: "1px solid #dce9df", borderRadius: 13, boxShadow: "0 8px 22px rgba(0,0,0,.08)" }}>
      <label style={{ color: C.textSub, fontSize: 12, fontWeight: 800 }}>Team <select value={teamFilter} onChange={(e) => setTeamFilter(e.target.value)} style={{ marginLeft: 6, padding: "7px 28px 7px 9px", borderRadius: 8, border: "1px solid #dce9df" }}><option value="ALL">Both teams</option>{payloads.map((team) => <option key={team.team} value={team.team}>{team.team_name}</option>)}</select></label>
      <label style={{ color: C.textSub, fontSize: 12, fontWeight: 800 }}>Position <select value={positionFilter} onChange={(e) => setPositionFilter(e.target.value)} style={{ marginLeft: 6, padding: "7px 28px 7px 9px", borderRadius: 8, border: "1px solid #dce9df" }}><option value="ALL">All positions</option>{positions.map((position) => <option key={position} value={position}>{position}</option>)}</select></label>
    </div>
    {visibleTeams.map((team) => { const visiblePlayers = team.players.filter((player) => positionFilter === "ALL" || (player.position_group || player.position) === positionFilter); return <section key={team.team} style={{ background: "#fff", border: `1px solid ${C.accentLight}`, borderRadius: 16, padding: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "baseline", marginBottom: 10 }}><h3 style={{ margin: 0, color: C.accentDark }}>{team.team_name}</h3><span style={{ color: C.textMut, fontSize: 12 }}>{team.season} regular season · click a player</span></div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 8 }}>
        {visiblePlayers.map((player) => <details key={player.id} style={{ border: "1px solid #e1ebe4", borderRadius: 12, background: "#f8fcf9" }}>
          <summary style={{ listStyle: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 10, padding: 11 }}>
            {player.headshot_url ? <img src={player.headshot_url} alt="" width={38} height={38} style={{ borderRadius: 99, objectFit: "cover", background: "#e9f3ec" }} /> : <span style={{ width: 38, height: 38, borderRadius: 99, display: "grid", placeItems: "center", background: C.accentLight, fontWeight: 900 }}>{player.position}</span>}
            <span><b style={{ color: C.textPri }}>{player.name}</b><small style={{ display: "block", color: C.textMut, marginTop: 2 }}>{player.position} · {player.games} games · <b style={{ color: C.accentDark }}>{playerPerGame(player)}</b></small></span><span style={{ marginLeft: "auto", color: C.accentDark, fontWeight: 900 }}>＋</span>
          </summary>
          <a style={{display:"block",padding:12,color:C.accentDark}} href={`/sports/nfl/player/${encodeURIComponent(player.id)}?fixture_id=${fixtureId}`}>View full player profile →</a>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 7, padding: "0 11px 11px" }}>{playerStatLines(player).map(([label, value]) => <div key={label} style={{ padding: 9, background: "#fff", borderRadius: 9 }}><small style={{ color: C.textMut, textTransform: "uppercase", fontWeight: 800 }}>{label}</small><b style={{ display: "block", marginTop: 3, color: C.textPri, fontSize: 17 }}>{value ?? "—"}</b></div>)}</div>
        </details>)}{!visiblePlayers.length ? <div style={{ color: C.textMut, padding: 16 }}>No {positionFilter} players for this team.</div> : null}
      </div>
    </section>})}
  </div>;
}

const emptyPanelStyle = { background: "#fff", borderRadius: 14, padding: 24, color: "#64748b", textAlign: "center" };

export default function NflGameDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [statsSeason, setStatsSeason] = useState("");
  const [edgePayload, setEdgePayload] = useState(null);
  const [activeTab, setActiveTab] = useState("Overview");

  useEffect(() => {
    setErr(""); setData(null);
    fetch(`${API_BASE}/api/fixtures/id/${id}/json${statsSeason ? `?stats_season=${statsSeason}` : ""}`, { cache: "no-store" })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(j => setData(j)).catch(e => setErr(e.message));
  }, [id, statsSeason]);

  useEffect(() => {
    let cancelled = false;
    fetchFixtureEdges(id).then((payload) => {
      if (!cancelled) setEdgePayload(payload || null);
    }).catch(() => {
      if (!cancelled) setEdgePayload(null);
    });
    return () => { cancelled = true; };
  }, [id]);

  const groupedOdds = useMemo(() => {
    const byBook = {};
    (data?.odds || []).forEach(o => {
      const book = (o.bookmaker || "other").toLowerCase();
      if (!byBook[book]) byBook[book] = [];
      byBook[book].push(o);
    });
    Object.keys(byBook).forEach(b => byBook[b].sort((a,b) => (a.market||"").localeCompare(b.market||"")));
    return byBook;
  }, [data]);

  const formatUTC = (iso) => {
    if (!iso) return "—";
    const s = /Z$|[+-]\d{2}:\d{2}$/.test(iso) ? iso : iso + "Z";
    try {
      return new Date(s).toLocaleString("en-GB", { timeZone: "UTC", weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  if (err) return <div style={{ position: "relative", minHeight: "100vh", background: C.pageBg }}><FieldBg /><div style={{ position: "relative", zIndex: 1, padding: 24 }}><p style={{ color: C.red }}>{err}</p></div></div>;
  if (!data) return <div style={{ position: "relative", minHeight: "100vh", background: C.pageBg }}><FieldBg /><div style={{ position: "relative", zIndex: 1, padding: 24 }}><p>Loading…</p></div></div>;
  if (!data.fixture) return <div style={{ position: "relative", minHeight: "100vh", background: C.pageBg }}><FieldBg /><div style={{ position: "relative", zIndex: 1, padding: 24 }}><p>No fixture data found.</p></div></div>;

  const f = data.fixture;
  const bestEdges = (edgePayload?.edges || edgePayload?.edges_teaser || data.best_edges || []).filter((edge) => isAllowedPublicEdge(edge?.edge));
  const accessMeta = edgePayload ? {
    isPremium: !!edgePayload.is_premium,
    hasAccess: !!edgePayload.has_access,
    hasFullAccess: !!(edgePayload.is_premium || edgePayload.has_access),
    isTeaser: !edgePayload.is_premium && !edgePayload.has_access,
    usedToday: edgePayload.used_today ?? 0,
    limit: edgePayload.limit ?? 0,
  } : null;
  const displayedStatsSeason = data.home_stats?.season || data.away_stats?.season || null;
  const selectedStatsSeason = Number(statsSeason || displayedStatsSeason || 0);
  const usingBaseline = displayedStatsSeason && selectedStatsSeason && Number(displayedStatsSeason) !== selectedStatsSeason;

  return (
    <div style={{ position: "relative", minHeight: "100vh", background: C.pageBg }}>
      <FieldBg />
      <div style={{ position: "relative", zIndex: 1, maxWidth: 960, margin: "0 auto", padding: "24px 16px" }}>
        <div style={{ marginBottom: 16 }}>
          <Link to="/nfl" style={{ color: C.accentDark, textDecoration: "none", fontSize: 14, fontWeight: 500 }}>← Back to NFL Games</Link>
        </div>

        <div style={{ background: "linear-gradient(135deg,#073d20,#07150d)", color: "#fff", border: "1px solid rgba(101,232,122,.24)", borderRadius: 20, padding: 28, marginBottom: 20, boxShadow: "0 22px 60px rgba(0,0,0,.24)" }}>
          <div style={{ marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <span style={{ display: "inline-block", background: "rgba(101,232,122,.16)", color: "#8ff29f", border: "1px solid rgba(101,232,122,.28)", padding: "5px 11px", borderRadius: 999, fontSize: 11, fontWeight: 900, textTransform: "uppercase", letterSpacing: "1px" }}>{f.comp || "NFL"} Game Centre</span>
            {accessMeta ? <FixtureAccessPill meta={accessMeta} fixtureId={id} /> : null}
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 16, fontSize: 28, fontWeight: 800, color: "#fff", marginBottom: 12, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-end" }}>
              {f.away_team_code ? <Link to={`/nfl/team/${f.away_team_code}`} style={{ color: "#fff", textDecoration: "none" }}>{f.away_team}</Link> : <span>{f.away_team}</span>}
              {f.away_logo ? <img src={f.away_logo} alt={f.away_team} width={52} height={52} style={{ objectFit: "contain" }} onError={e => e.currentTarget.style.display = "none"} /> : <Football size={30} />}
            </div>
            <span style={{ color: C.textMut, fontWeight: 700, fontSize: 15 }}>AT</span>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {f.home_logo ? <img src={f.home_logo} alt={f.home_team} width={52} height={52} style={{ objectFit: "contain" }} onError={e => e.currentTarget.style.display = "none"} /> : <Football size={30} />}
              {f.home_team_code ? <Link to={`/nfl/team/${f.home_team_code}`} style={{ color: "#fff", textDecoration: "none" }}>{f.home_team}</Link> : <span>{f.home_team}</span>}
            </div>
          </div>
          <div style={{ textAlign: "center", color: "#b8cbbf", fontSize: 14 }}>{formatUTC(f.kickoff_utc)} UTC</div>
        </div>

        <nav style={{ position: "sticky", top: 76, zIndex: 5, display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 6, marginBottom: 14, padding: 6, borderRadius: 14, background: "rgba(255,255,255,.94)", border: "1px solid #dce9df", boxShadow: "0 10px 28px rgba(0,0,0,.10)", backdropFilter: "blur(12px)" }}>
          {["Overview", "Edges", "Odds", "Players"].map((tab) => <button key={tab} onClick={() => setActiveTab(tab)} style={{ border: 0, borderRadius: 10, padding: "11px 8px", cursor: "pointer", fontWeight: 900, background: activeTab === tab ? C.accentDark : "transparent", color: activeTab === tab ? "#fff" : C.textSub }}>{tab}{tab === "Edges" && bestEdges.length ? ` · ${bestEdges.length}` : ""}</button>)}
        </nav>

        {activeTab === "Overview" ? <>
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <label htmlFor="nfl-stats-season" style={{ color: C.textSub, fontSize: 12, fontWeight: 700 }}>Stats season</label>
          <select id="nfl-stats-season" value={statsSeason || data.home_stats?.season || data.away_stats?.season || ""} onChange={(e) => setStatsSeason(e.target.value)} style={{ padding: "8px 30px 8px 10px", border: `1px solid ${C.accentLight}`, borderRadius: 9, background: "#fff", color: C.textPri, fontWeight: 800 }}>
            {(data.stats_seasons || []).map((year) => <option key={year} value={year}>{year}</option>)}
          </select>
        </div>
        {usingBaseline ? <div style={{ marginBottom: 12, padding: "11px 14px", borderRadius: 11, border: "1px solid #e2c46f", background: "#fff9e7", color: "#715613", fontSize: 13 }}><b>{selectedStatsSeason} regular-season results are not available yet.</b> Showing the completed {displayedStatsSeason} baseline for this preseason matchup.</div> : null}
        <RecordedSportStats fixtureId={id} sport="nfl" />
        <MatchupModelSection
          fixtureId={id}
          sport="nfl"
          fixture={f}
          homeStats={data.home_stats}
          awayStats={data.away_stats}
          predictions={data.predictions}
          modelProbs={data.model_probs}
          theme={{
            accent: C.accentDark,
            accentSoft: C.accentLight,
            accentBorder: "rgba(22,101,52,0.20)",
            surface: "rgba(255,255,255,0.96)",
            text: C.textPri,
            textMuted: C.textSub,
            border: "rgba(22,101,52,0.16)",
            positive: C.green,
            negative: C.red,
          }}
        />
        </> : null}

        {activeTab === "Players" ? <PlayerStatsPanel fixture={f} fixtureId={id} season={selectedStatsSeason || displayedStatsSeason} /> : null}

        {activeTab === "Edges" ? <div style={{ background: C.cardBg, borderRadius: 16, padding: 24, marginBottom: 20, boxShadow: "0 10px 30px rgba(0,0,0,0.10)" }}>
          <h3 style={{ margin: "0 0 16px", color: C.accentDark, fontSize: 18 }}>🏈 Best Edges</h3>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${C.fieldLine}` }}>
                <th align="left" style={{ padding: "8px 0", color: C.textSub, fontWeight: 600, fontSize: 13 }}>Market</th>
                <th align="right" style={{ padding: "8px 0", color: C.textSub, fontWeight: 600, fontSize: 13 }}>Odds</th>
                <th align="right" style={{ padding: "8px 0", color: C.textSub, fontWeight: 600, fontSize: 13 }}>Edge</th>
                <th align="left" style={{ padding: "8px 0", color: C.textSub, fontWeight: 600, fontSize: 13 }}>Bookmaker</th>
              </tr>
            </thead>
            <tbody>
              {bestEdges.map((e,i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${C.accentLight}` }}>
                  <td style={{ padding: "10px 0", color: C.textPri, fontWeight: 500 }}>{e.market}</td>
                  <td align="right" style={{ padding: "10px 0", color: C.textPri }}>{Number(e.price).toFixed(2)}</td>
                  <td align="right" style={{ padding: "10px 0", color: C.green, fontWeight: 600 }}>+{(Number(e.edge)*100).toFixed(1)}%</td>
                  <td style={{ padding: "10px 0 10px 18px", color: C.textSub, textTransform: "capitalize" }}>{e.bookmaker}</td>
                </tr>
              ))}
              {bestEdges.length === 0 && (
                <tr><td colSpan={4} style={{ padding: "16px 0", color: C.textMut, textAlign: "center" }}>No edges found yet.</td></tr>
              )}
            </tbody>
          </table>
        </div> : null}

        {activeTab === "Odds" ? <div style={{ background: C.cardBg, borderRadius: 16, padding: 24, boxShadow: "0 10px 30px rgba(0,0,0,0.10)" }}>
          <h3 style={{ margin: "0 0 5px", color: C.accentDark, fontSize: 18 }}>📊 Sportsbook prices</h3>
          <p style={{ margin: "0 0 16px", color: C.textMut, fontSize: 13 }}>Books are collapsed by default. Open one to inspect every captured market.</p>
          {Object.keys(groupedOdds).length === 0 ? <p style={{ color: C.textMut }}>No odds captured yet.</p> : (
            Object.entries(groupedOdds).map(([book, rows]) => (
              <details key={book} style={{ marginBottom: 9, border: "1px solid #dce9df", borderRadius: 12, overflow: "hidden" }}>
                <summary style={{ cursor: "pointer", padding: "13px 15px", fontWeight: 800, textTransform: "capitalize", color: C.textPri, fontSize: 15, background: "#f8fcf9" }}>{book}<span style={{ float: "right", color: C.textMut, fontSize: 12 }}>{rows.length} markets ＋</span></summary>
                <div style={{ padding: "0 14px 12px", overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${C.accentLight}` }}>
                      <th align="left" style={{ padding: "6px 0", color: C.textSub, fontWeight: 500, fontSize: 12 }}>Market</th>
                      <th align="right" style={{ padding: "6px 0", color: C.textSub, fontWeight: 500, fontSize: 12 }}>Price</th>
                      <th align="left" style={{ padding: "6px 0", color: C.textSub, fontWeight: 500, fontSize: 12 }}>Last Seen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((o,idx) => {
                      const seen = o.last_seen ? (/Z$|[+-]\d{2}:\d{2}$/.test(o.last_seen) ? o.last_seen : o.last_seen + "Z") : "";
                      return (
                        <tr key={`${book}-${idx}`} style={{ borderBottom: "1px solid #f1f5f9" }}>
                          <td style={{ padding: "8px 0", color: C.textPri, fontSize: 14 }}>{o.market}</td>
                          <td align="right" style={{ padding: "8px 18px 8px 0", color: C.textPri, fontSize: 14 }}>{Number(o.price).toFixed(2)}</td>
                          <td style={{ padding: "8px 0 8px 18px", color: C.textMut, fontSize: 13 }}>{seen ? new Date(seen).toLocaleString("en-GB") : "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              </details>
            ))
          )}
        </div> : null}

        <p style={{ fontSize: 13, color: C.textMut, marginTop: 16, textAlign: "center" }}>Season baselines use nflverse; prices and model leans are tied to this fixture.</p>
      </div>
    </div>
  );
}
